import { assignUserToLead, bulkReassignLeads, changeLeadStatus, createLead, editLead, fetchLeadDetails, fetchLeadLeads, updateCustomFields, removeLeadCollaborator } from "./leads.thunks";

import { LeadState } from "@grudder/types/leads.type";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { reset } from "../reset";
import dayjs from "dayjs";

const initialState: LeadState = {
  currentLeadIds: [],
  leads: [],
  total: 0,
  loading: false,
  listLoading: null,
  assignLoading: [],
  cdtEditLoading: [],
  bulkActionLoading: false, // Flag to indicate if a bulk action is in progress
  editLoading: [],
  statusChangingLoading: [],
  collaboratorRemoving: [], // composite keys `${leadId}:${userId}` while removing
  error: null,
  activeSavedFilterId: "",
  activeFilterRules: [],
  isFetched: false, // Flag to indicate if leads have been fetched
  searchKey: null,// Flag to indicate if a bulk action is in progress

};

const leadSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setInitialLeads: (state, action: PayloadAction<{ data: any[]; total: number }>) => {
      const leadMap = new Map(state.leads.map((lead) => [lead.id, lead]));
      action.payload.data.forEach((lead) => {
        // check if current is latest
        const currentLead = leadMap.get(lead.id);
        if (currentLead && new Date(currentLead.updatedAt).getTime() >= new Date(lead.updatedAt).getTime()) {
          return;
        }
        leadMap.set(lead.id, lead);
      });

      state.leads = Array.from(leadMap.values());
      state.isFetched = true;
      state.currentLeadIds = action.payload.data.map((lead) => lead.id);
      state.total = action.payload.total;
    },
    updateLeadDetails: (state, action: PayloadAction<any | null>) => {
      // Create a Map from the current leads array for quick lookups
      const leadsMap = new Map(state.leads.map((lead) => [lead._id, lead]));

      const newLead = action.payload;
      const existingLead = leadsMap.get(newLead._id);

      // If the lead exists and the new lead data is new, update the Map
      if (existingLead) {
        if (new Date(existingLead.updatedAt).getTime() <= new Date(newLead.updatedAt).getTime()) {
          console.log("lead is New, updating the existing lead in the Map");
          leadsMap.set(newLead._id, newLead);
        } else {
          console.log("Existing lead is newer, keeping the old data");
        }
      } else {
        // If the lead does not exist, add it to the Map
        console.log("Lead does not exist, adding new lead to the Map");
        leadsMap.set(newLead._id, newLead);
      }

      // Convert the Map back to an array and update the state
      state.leads = Array.from(leadsMap.values());
    },
    setListLoading: (state, action: PayloadAction<string | null>) => {
      state.listLoading = action.payload;
    },
    setLeadFilter: (state, action: PayloadAction<{ filterId?: string; rules: any[] }>) => {
      state.activeSavedFilterId = action.payload.filterId || "";

      state.activeFilterRules = action.payload.rules;
    },
    setLeadSearchKey: (state, action: PayloadAction<string | null>) => {
      state.searchKey = action.payload;
    },
    resetLeadSlice: (state) => {
      Object.assign(state, initialState);
    },
    // Optimistically remove a collaborator from a lead
    optimisticRemoveCollaborator: (state, action: PayloadAction<{ leadId: string; collaboratorId: string }>) => {
      const { leadId, collaboratorId } = action.payload;
      const lead = state.leads.find((l: any) => l._id === leadId);
      if (lead) {
        lead.collaborators = (lead.collaborators || []).filter((c: any) => c?._id !== collaboratorId && c?.id !== collaboratorId);
      }
    },
    // Rollback collaborators list if API failed
    rollbackCollaborators: (state, action: PayloadAction<{ leadId: string; previous: any[] }>) => {
      const { leadId, previous } = action.payload;
      const lead = state.leads.find((l: any) => l._id === leadId);
      if (lead) {
        lead.collaborators = previous;
      }
    },
    // Track loading state for a specific collaborator removal
    setCollaboratorRemoving: (state, action: PayloadAction<{ leadId: string; collaboratorId: string; removing: boolean }>) => {
      const { leadId, collaboratorId, removing } = action.payload;
      const key = `${leadId}:${collaboratorId}`;
      if (!state.collaboratorRemoving) state.collaboratorRemoving = [];
      if (removing) {
        if (!state.collaboratorRemoving.includes(key)) state.collaboratorRemoving.push(key);
      } else {
        state.collaboratorRemoving = state.collaboratorRemoving.filter((k: string) => k !== key);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeadDetails.pending, (state, action) => {
        state.loading = true;
        const existingLead = state.leads.find((lead: any) => lead._id === action.meta.arg);
        if (existingLead) {
          state.loading = false;
        }
      })
      .addCase(fetchLeadDetails.fulfilled, (state, action) => {
        // Create a Map from the current leads array for quick lookups
        const leadsMap = new Map(state.leads.map((lead) => [lead._id, lead]));

        const newLead = action.payload;
        const existingLead = leadsMap.get(newLead._id);

        // If the lead exists and the new lead data is new, update the Map
        if (existingLead) {
          if (new Date(existingLead.updatedAt).getTime() <= new Date(newLead.updatedAt).getTime()) {
            console.log("New lead is newer, updating the existing lead in the Map");
            leadsMap.set(newLead._id, newLead);
          } else {
            console.log("Existing lead is newer, keeping the old data");
          }
        } else {
          // If the lead does not exist, add it to the Map
          console.log("Lead does not exist, adding new lead to the Map");
          leadsMap.set(newLead._id, newLead);
        }

        // Convert the Map back to an array and update the state
        state.leads = Array.from(leadsMap.values());
        state.loading = false;
      })
      .addCase(fetchLeadDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(assignUserToLead.pending, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.assignLoading.push(leadId);
        state.error = null;
      })
      .addCase(assignUserToLead.fulfilled, (state, action) => {
        const leadId = action.meta.arg.leadId;
        const leadsMap = new Map(state.leads.map((lead) => [lead._id, lead]));
        leadsMap.set(action.payload._id, action.payload);
        state.assignLoading = state.assignLoading.filter((assignItem) => assignItem !== leadId);
        state.leads = Array.from(leadsMap.values());
      })
      .addCase(assignUserToLead.rejected, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.assignLoading = state.assignLoading.filter((assignItem) => assignItem !== leadId);
        state.error = action.payload;
      })
      .addCase(changeLeadStatus.pending, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.statusChangingLoading.push(leadId);
        state.error = null;
      })
      .addCase(changeLeadStatus.fulfilled, (state, action) => {
        const leadId = action.meta.arg.leadId;
        const leadsMap = new Map(state.leads.map((lead) => [lead._id, lead]));
        leadsMap.set(action.payload._id, action.payload);
        state.statusChangingLoading = state.statusChangingLoading.filter((statusItem) => statusItem !== leadId);
        state.leads = Array.from(leadsMap.values());

        // Update the action status to 'completed'
      })
      .addCase(changeLeadStatus.rejected, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.statusChangingLoading = state.statusChangingLoading.filter((statusItem) => statusItem !== leadId);
        state.error = action.payload;

        // Update the action status to 'failed'
      })
      .addCase(updateCustomFields.pending, (state, action) => {
        const moduleId = action.meta.arg.moduleId;
        if (moduleId) {
          state.cdtEditLoading.push(moduleId);
        }
        state.error = null;
      })
      .addCase(updateCustomFields.fulfilled, (state, action) => {
        const moduleId = action.meta.arg.moduleId;
        const leadsMap = new Map(state.leads.map((lead) => [lead._id, lead]));
        leadsMap.set(action.payload._id, action.payload);
        state.cdtEditLoading = state.cdtEditLoading.filter((cdtItem) => cdtItem !== moduleId);
        state.leads = Array.from(leadsMap.values());
      })
      .addCase(updateCustomFields.rejected, (state, action) => {
        const moduleId = action.meta.arg.moduleId;
        state.cdtEditLoading = state.cdtEditLoading.filter((cdtItem) => cdtItem !== moduleId);
        state.error = action.payload;
      })
      .addCase(editLead.pending, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.editLoading.push(leadId);
        state.error = null;
      })
      .addCase(editLead.fulfilled, (state, action) => {
        const leadId = action.meta.arg.leadId;
        const leadsMap = new Map(state.leads.map((lead) => [lead._id, lead]));
        leadsMap.set(action.payload._id, action.payload);
        state.editLoading = state.editLoading.filter((editItem) => editItem !== leadId);
        state.leads = Array.from(leadsMap.values());
      })
      .addCase(editLead.rejected, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.editLoading = state.editLoading.filter((editItem) => editItem !== leadId);
        state.error = action.payload;
      })
      //create lead cases
      .addCase(createLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        const newLead = action.payload;
        state.leads = [newLead, ...state.leads.filter((l: any) => l._id !== newLead._id)];
        state.loading = false;
        state.currentLeadIds = [newLead._id, ...state.currentLeadIds.filter((id) => id !== newLead._id)];
      })
      .addCase(createLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchLeadLeads.pending, (state) => {
        state.listLoading = dayjs().format("YYYY-MM-DD HH:mm:ss");
        state.error = null;
      })
      .addCase(fetchLeadLeads.fulfilled, (state, action) => {
        // const leadMap = new Map(state.leads.map((lead) => [lead._id, lead]));
        // action.payload.data.forEach((lead: any) => {
        //     // check if current is latest
        //     const currentLead = leadMap.get(lead._id);
        //     if (currentLead && new Date(currentLead.updatedAt).getTime() >= new Date(lead.updatedAt).getTime()) {
        //         return;
        //     }
        //     leadMap.set(lead._id, lead);
        // });
        state.total = action.payload.total;
        state.leads = action.payload.data;
        //state.currentLeadIds = action.payload.data.map((lead: any) => lead.id);
        state.listLoading = null;
      })
      .addCase(fetchLeadLeads.rejected, (state, action) => {
        state.listLoading = null;
        state.error = action.payload;
      })
      .addCase(bulkReassignLeads.pending, (state) => {
        state.bulkActionLoading = true;
      })
      .addCase(bulkReassignLeads.fulfilled, (state) => {
        state.bulkActionLoading = false;
      })
      .addCase(bulkReassignLeads.rejected, (state) => {
        state.bulkActionLoading = false;
      })
      // collaborator removal update lead with new collaborators array
      .addCase(removeLeadCollaborator.fulfilled, (state, action) => {
        const leadsMap = new Map(state.leads.map((lead) => [lead._id, lead]));
        leadsMap.set(action.payload._id, action.payload);
        state.leads = Array.from(leadsMap.values());
      })

      .addCase(reset, () => initialState); // Handle reset action
  },
});
export const { resetLeadSlice, setInitialLeads, updateLeadDetails, setListLoading, setLeadFilter, setLeadSearchKey, optimisticRemoveCollaborator, rollbackCollaborators, setCollaboratorRemoving } = leadSlice.actions;

export default leadSlice.reducer;
